import { ChangeDetectionStrategy, Component, computed, inject, input, signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DividerComponent } from '@shared/components/divider.component';
import { TitleContainerComponent } from '@shared/components/title-container.component';
import { AssessmentsApiService } from '../services/assessments-api.service';
import { distinctUntilChanged, exhaustMap, forkJoin, map, merge, of, Subject, switchMap, take, takeWhile, tap, timer } from 'rxjs';
import { AssessmentDetail } from '../models/assessment.model';
import { ABSTAIN_ID, HIGH_RISK_ID, LOW_RISK_ID, MANDATORY_ID, MEDIUM_RISK_ID } from '@shared/models/sensitivity-level.model';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { QuillViewComponent } from 'ngx-quill';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { ItemViewContainerComponent } from '@shared/components/item-view-container.component';
import { CommonModule } from '@angular/common';
import { GroupedCountrySectorAdvice, GroupedJoinedCountrySectorAdvice } from '../models/grouped-country-sector-advice.model';
import { MainLobAdvice } from '../models/main-lob-advice.model';
import { environment } from 'src/environments/environment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { PaTableComponent } from '../components/pa-table.component';
import { AsyncState, AsyncStateEnum } from '@shared/models/async-state.enum';
import { Router } from '@angular/router';
import { getFilenameFromContentDisposition, saveFile } from '@shared/utils/file.utils';
import { AuthService } from '@core/auth/auth.service';
import { InsightsPanelComponent } from '../insights/insights-panel.component';
import { InsightsAssessmentContext } from '../insights/models/insights.model';
import { ESG_RISK_SOURCE_SYSTEM, OTHER_PROJECT_ID } from '@shared/constants';

@Component({
  selector: 'app-assessment-detail',
  imports: [
    TitleContainerComponent,
    DividerComponent,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    QuillViewComponent,
    MatExpansionModule,
    ItemViewContainerComponent,
    CommonModule,
    DividerComponent,
    PaTableComponent,
    InsightsPanelComponent,
  ],
  providers: [AssessmentsApiService],
  templateUrl: './assessment-detail.component.html',
  styleUrl: './assessment-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssessmentDetailComponent {
  readonly ABSTAIN_ID = ABSTAIN_ID;
  readonly OTHER_PROJECT_ID = OTHER_PROJECT_ID;

  readonly expansionPanel = viewChild<MatExpansionPanel>('expansionPanel');
  readonly insightsPanelComponent = viewChild(InsightsPanelComponent);

  readonly assessmentsApiService = inject(AssessmentsApiService);
  readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  readonly assessmentId = input.required<number>();

  protected readonly autoGenerateInsights = this.consumeAutoGenerateInsightsFlag();

  isLoading = signal(true);
  assessmentState = signal<AsyncState>(AsyncStateEnum.FINISHED);
  asyncStateEnum = AsyncStateEnum;

  private pollTrigger$ = new Subject<void>();

  private consumeAutoGenerateInsightsFlag(): boolean {
    const { autoGenerateInsights, ...rest } = history.state ?? {};
    const shouldAutoGenerate = autoGenerateInsights === true;
    if (shouldAutoGenerate) {
      history.replaceState(rest, '', location.href);
    }
    return shouldAutoGenerate;
  }

  assessmentDetail = toSignal<AssessmentDetail>(
    toObservable(this.assessmentId).pipe(
      tap(() => this.isLoading.set(true)),
      switchMap(id =>
        // initial + retriggers
        merge(of(void 0), this.pollTrigger$).pipe(
          switchMap(() =>
            timer(0, 3000).pipe(
              exhaustMap(() =>
                this.assessmentsApiService.getAssessmentDetailById$(id).pipe(
                  map(assessment => {
                    // workaround for the backend not returning the assessment state
                    assessment = { ...assessment, assessmentState: assessment.assessmentState || AsyncStateEnum.FINISHED };
                    if (assessment.assessmentState === AsyncStateEnum.FINISHED) {
                      return {
                        ...assessment,
                        sensitivityLevel: {
                          ...assessment.sensitivityLevel,
                          common: assessment.sensitivityLevel.common.replace(/Main Driver/g, assessment.mainDriver.text),
                        },
                        mainLobSensLvlAdvice: this.updateLinkMainLobAdvice(assessment.mainLobSensLvlAdvice),
                      };
                    }
                    return assessment;
                  })
                )
              ),
              distinctUntilChanged((prev, curr) => prev.assessmentState === curr.assessmentState),
              tap(assessment => {
                this.assessmentState.set(assessment.assessmentState);
                if (assessment.assessmentState !== AsyncStateEnum.IN_PROGRESS) {
                  this.isLoading.set(false);
                }
              }),
              takeWhile(
                assessment => assessment.assessmentState === AsyncStateEnum.IN_PROGRESS,
                true // ← emit terminal value, then complete
              )
            )
          )
        )
      )
    )
  );

  imageLink = computed(() => {
    const sensitivityId = this.assessmentDetail()?.sensitivityLevel.id;

    switch (sensitivityId) {
      case ABSTAIN_ID:
        return 'assets/images/SensitivityAbstain.png';
      case HIGH_RISK_ID:
        return 'assets/images/SensitivityHigh.png';
      case MEDIUM_RISK_ID:
        return 'assets/images/SensitivityMedium.png';
      case LOW_RISK_ID:
        return 'assets/images/SensitivityLow.png';
      case MANDATORY_ID:
        return 'assets/images/SensitivityEsgMandatory.png';
      default:
        return 'not-found';
    }
  });

  companyView = computed(() =>
    this.assessmentDetail()?.otherCompany && !this.assessmentDetail()?.matchingMessage
      ? this.assessmentDetail()?.otherCompany
      : this.assessmentDetail()?.company?.name
  );

  projectView = computed(() =>
    this.assessmentDetail()?.otherProject && !this.assessmentDetail()?.projectMatchingMessage
      ? this.assessmentDetail()?.otherProject
      : this.assessmentDetail()?.project?.name
  );

  countrySectorAdvices = computed<GroupedJoinedCountrySectorAdvice[]>(
    () =>
      this.assessmentDetail()
        ?.groupedCountryIndustryAdvices?.filter(item => !item.advice.hrRelated)
        .map(item => this.joinAndUpdateCountrySectorAdvice(item)) ?? []
  );

  hrCountrySectorAdvice = computed<GroupedJoinedCountrySectorAdvice[]>(
    () =>
      this.assessmentDetail()
        ?.groupedCountryIndustryAdvices?.filter(item => item.advice.hrRelated)
        .map(item => this.joinAndUpdateCountrySectorAdvice(item)) ?? []
  );

  protectedAreaSummaryText = computed(() => {
    const protectedAreas = this.assessmentDetail()?.assessmentProtectedAreas;
    const base = `${protectedAreas?.totalAmount} locations have been submitted for the ESG Risk assessment, of those - ${protectedAreas?.amountOfHits} are located within protected areas.`;
    return (protectedAreas?.amountOfHits ?? 0) <= 0 ? base : `${base} Please see below the details.`;
  });

  hasProtectedAreas = computed(() => {
    const totalProtectedAreas = this.assessmentDetail()?.assessmentProtectedAreas?.totalAmount || 0;
    return totalProtectedAreas > 0;
  });

  isThirdPartyMediumRisk = computed(() => this.assessmentDetail()?.isThirdParty === true && this.isMediumRisk());
  mainLineOfBusinessDetails = computed(() =>
    this.assessmentDetail()?.isThirdParty ? 'Third Parties' : this.assessmentDetail()?.mainLineOfBusiness?.name
  );

  private sensitivityId = computed(() => this.assessmentDetail()?.sensitivityLevel?.id);
  private isMediumRisk = computed(() => this.sensitivityId() === MEDIUM_RISK_ID);
  private isHighRisk = computed(() => this.sensitivityId() === HIGH_RISK_ID);
  private roleAllowsInsights = computed(() => this.isMediumRisk() || (this.isHighRisk() && this.authService.isRiskManager()));

  canManuallyGenerateInsights = computed(
    () =>
      this.isHighRisk() &&
      this.authService.isRiskManager() &&
      this.authService.isInsightsAuthEligible() &&
      this.authService.hasInsightsAccess() &&
      this.assessmentDetail()?.dealSourceSystem !== ESG_RISK_SOURCE_SYSTEM
  );

  /** Whether the insights panel can be shown to the current user for this assessment. */
  canViewInsights = computed(() => this.roleAllowsInsights());
  /** Medium-risk insights replace the advice panel; otherwise advice stays.
   * Exception: if insights have nothing to display, the insights panel is hidden and advice is shown instead. */
  showAdvicePanel = computed(() => {
    if (!(this.isMediumRisk() && this.canViewInsights())) {
      return true;
    }
    return this.insightsPanelComponent()?.store.requiresAdviceFallback() ?? false;
  });
  insightsContext = computed<InsightsAssessmentContext | undefined>(() => {
    const detail = this.assessmentDetail();
    if (!detail) {
      return undefined;
    }
    return {
      assessmentId: String(detail.id),
      company: this.companyView() ?? '',
      countries: detail.countries?.map(country => country.name) ?? [],
      sector: detail.industrySegment?.name ?? '',
      project: detail.project?.sdlId === this.OTHER_PROJECT_ID && !detail.otherProject ? '' : (this.projectView() ?? ''),
    };
  });

  private joinAndUpdateCountrySectorAdvice(advice: GroupedCountrySectorAdvice): GroupedJoinedCountrySectorAdvice {
    return {
      advice: advice.advice,
      joinedCountries: advice.countries.map(country => country.name).join(', '),
      countries: advice.countries,
    };
  }

  // add assessment query param to referral link
  private updateLinkMainLobAdvice(mainLobAdvice: MainLobAdvice | undefined): MainLobAdvice | undefined {
    if (!mainLobAdvice?.advice) {
      return undefined;
    }
    return {
      ...mainLobAdvice,
      advice: this.addAssessmentIdToReferralUrl(mainLobAdvice.advice),
    };
  }

  private addAssessmentIdToReferralUrl(text: string) {
    const link = `${environment.esgReferralLink}/create?assessmentId=${this.assessmentId()}`;
    return text.replace(/href="(https:\/\/esg-risk.*?-referral.*?swissre\.com.*?)"/g, `href="${link}"`);
  }

  generateFiles() {
    this.generatePDF();
    if (this.hasProtectedAreas()) {
      this.downloadPACoordinates();
    }
  }

  generatePDF() {
    const content = document.getElementById('pdf-content');
    if (!content) return;

    const panelsToOpen: MatExpansionPanel[] = [];

    const advicePanel = this.expansionPanel();
    if (advicePanel && !advicePanel.expanded) panelsToOpen.push(advicePanel);

    const insightsPanel = this.insightsPanelComponent()?.panel();
    if (insightsPanel && !insightsPanel.expanded) panelsToOpen.push(insightsPanel);

    if (panelsToOpen.length === 0) {
      this.capturePDF(content);
      return;
    }

    forkJoin(panelsToOpen.map(p => p.afterExpand.pipe(take(1))))
      .pipe(tap(() => this.capturePDF(content)))
      .subscribe();

    panelsToOpen.forEach(p => p.open());
  }

  private capturePDF(content: HTMLElement) {
    html2canvas(content, {
      scale: 2,
      scrollY: -window.scrollY,
      useCORS: true,
    }).then(canvas => {
      const imgProps = {
        width: canvas.width,
        height: canvas.height,
      };

      const pdf = new jsPDF('p', 'mm', 'a4');
      const padding = 10;

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const printableWidth = pageWidth - 2 * padding;
      const printableHeight = pageHeight - 2 * padding;

      const ratio = printableWidth / imgProps.width;
      const scaledHeight = imgProps.height * ratio;
      const totalPages = Math.ceil(scaledHeight / printableHeight);

      for (let i = 0; i < totalPages; i++) {
        const pageCanvas = document.createElement('canvas');
        const context = pageCanvas.getContext('2d')!;
        const sliceHeight = printableHeight / ratio; // in original canvas scale

        pageCanvas.width = imgProps.width;
        pageCanvas.height = sliceHeight;

        // Fill background with white so there's no transparency
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        context.drawImage(canvas, 0, i * pageCanvas.height, pageCanvas.width, pageCanvas.height, 0, 0, pageCanvas.width, pageCanvas.height);

        const imgData = pageCanvas.toDataURL('image/jpeg');
        if (i > 0) pdf.addPage();

        pdf.addImage(imgData, 'JPEG', padding, padding, printableWidth, printableHeight);
      }

      pdf.save(`${this.assessmentId()}-esgrisk-assessment-detail.pdf`);
    });
  }

  downloadPACoordinates() {
    this.assessmentsApiService
      .exportProtectedAreas$(this.assessmentId())
      .pipe(
        tap(response => {
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = getFilenameFromContentDisposition(contentDisposition) ?? 'downloaded-file';
          if (response.body) {
            saveFile(response.body, filename);
          }
        })
      )
      .subscribe();
  }

  navigateToAssessmentCreation() {
    this.router.navigate(['/assessments/new']);
  }

  recalculateAssessment() {
    this.isLoading.set(true);
    this.assessmentsApiService
      .recalculateAssessment$(this.assessmentId())
      .pipe(
        tap(assessment => {
          this.assessmentState.set(assessment.assessmentState);
          this.pollTrigger$.next();
        })
      )
      .subscribe();
  }

  createReferral() {
    const url = `${environment.esgReferralLink}/create?assessmentId=${this.assessmentId()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
